import { Ref, computed } from "vue";

import { useAuth } from "@/lib/useAuth";
import { useRfp, useRfpPhases } from "@/lib/useRfp";
import { Proposal } from "@/lib/useProposal";

import { RfpCore, RfpPhaseType } from "@/types/Marketplace";

export function usePolicyProject(rfp: Ref<RfpCore | undefined>) {
  const { isSuper, accountId, buyerId, isAuthed } = useAuth();
  const ownsRFP = computed(
    () => buyerId.value && buyerId.value === rfp.value?.buyer_id,
  );

  const reactiveRfpId = computed(() => rfp.value?.id);
  const { isPublicEntity } = useRfp(reactiveRfpId);
  const {
    activePhases,
    postSubmissionPeriod,
    inSubmissionPeriod,
    inShortlistSubmissionPeriod,
    phases,
  } = useRfpPhases(reactiveRfpId);

  const phasesLoaded = computed(() => !!phases.value);

  const canViewRfpAnalysis = computed(
    () => (ownsRFP.value && postSubmissionPeriod.value) || isSuper.value,
  );

  const canAccessQuestions = computed(() => {
    return (
      (isAuthed.value && activePhases.value?.includes("qa_period")) ?? false
    );
  });

  const canManageRfp = computed(() => ownsRFP.value || isSuper.value);

  const canEditFormStructure = computed(() => {
    return isSuper.value;
  });
  const canEditProjectPhases = computed(() => {
    return isSuper.value;
  });

  const inUserEditingPhase = computed(() => {
    // We've decided that superusers can always edit the form structure
    // but in these cases we should show a warning
    const userEditingPhases: RfpPhaseType[] = [
      "first_round_submission_period",
      "fixing_period",
      "shortlist_submission_period",
      "shortlist_fixing_period",
    ];
    const userEditingPhaseActive = activePhases.value?.some(
      (phaseType: RfpPhaseType) => userEditingPhases.includes(phaseType),
    );
    return userEditingPhaseActive;
  });

  const canAccessProjectContent = computed(() => {
    return accountId.value || isPublicEntity.value;
  });

  // superusers and buyer owners can always download the form
  // everyone else can only download the form if the RFP is in a submission period
  const canDownloadFormCsv = computed(() => {
    return (
      isSuper.value ||
      ownsRFP.value ||
      inSubmissionPeriod.value ||
      inShortlistSubmissionPeriod.value
    );
  });

  return {
    phasesLoaded,
    ownsRFP,
    canViewRfpAnalysis,
    canAccessProjectContent,
    canEditProjectPhases,
    canEditFormStructure,
    inUserEditingPhase,
    canAccessQuestions,
    canDownloadFormCsv,
    canManageRfp,
  };
}

export function usePolicyProvider(
  providerId: Ref<string | undefined>,
  managementMode: Ref<boolean>,
) {
  const { providerId: myProviderId, isSuper } = useAuth();

  const viewingSelf = computed(() => {
    return providerId.value === myProviderId.value;
  });

  const canEditProviderProfile = computed(() => {
    return viewingSelf.value || (isSuper.value && managementMode.value);
  });
  const managementModeAccessible = computed(() => {
    return !viewingSelf.value && isSuper.value;
  });

  return {
    viewingSelf,
    canEditProviderProfile,
    managementModeAccessible,
  };
}

export function usePolicyProposal(
  rfp: Ref<RfpCore | undefined>,
  proposal: Ref<Proposal | undefined>,
  managementMode: Ref<boolean | undefined>,
) {
  const reactiveRfpId = computed(() => rfp.value?.id);
  const {
    inSubmissionPeriod,
    inShortlistSubmissionPeriod,
    inFixingPeriod,
    firstRoundDeadline,
    shortlistDeadline,
    firstRoundFixingPeriod,
    shortlistFixingPeriod,
  } = useRfpPhases(reactiveRfpId);
  const { isSuper, providerId, isProvider, buyerId } = useAuth();
  const ownsRFP = computed(() => buyerId.value === rfp.value?.buyer_id);

  const ownsProposal = computed(() => {
    // note that myProviderId.value === undefined when the user is a buyer or station A user
    // so we should check isProvider is undefined also to see if we've got data or not
    // this is a bit silly since user provider info should be accesible immediately regardless
    if (
      (providerId.value === undefined && isProvider.value === undefined) ||
      proposal.value?.providerId === undefined
    ) {
      return undefined;
    }
    return providerId.value === proposal.value?.providerId;
  });

  const nextDeadline = computed(() => {
    if (!proposal.value) {
      return;
    }
    if (proposal.value.shortlisted) {
      return shortlistDeadline.value;
    } else {
      return firstRoundDeadline.value;
    }
  });

  const afterDeadline = computed(() => {
    if (!nextDeadline.value) {
      return;
    }
    const today = new Date();
    return today >= nextDeadline.value;
  });

  const backendThinksProposalCanBeSubmitted = computed(
    () =>
      proposal.value?.isFirstRoundSubmittable ||
      proposal.value?.isShortlistSubmittable,
  );

  const proposalHasBeenSubmitted = computed(() =>
    proposal.value?.shortlisted
      ? !!proposal.value?.shortlistSubmitted
      : !!proposal.value?.submitted,
  );

  const canViewStationAFixing = computed(() => isSuper.value);

  const canViewProviderFixing = computed(
    // For proposal owners, they should only see fixing flags within the fixing phase
    () => isSuper.value || (ownsProposal.value && inFixingPeriod.value),
  );

  // Basically superusers don't want to see the provider fixing controls
  // because they have their own controls
  const canUseProviderFixingControls = computed(
    () => ownsProposal.value && inFixingPeriod.value,
  );

  const proposalOverallIsReadOnly = computed(() => {
    if (
      proposalHasBeenSubmitted.value === undefined ||
      ownsProposal.value === undefined ||
      inEitherSubmissionPeriod.value === undefined
    ) {
      return;
    } else if (isSuper.value && managementMode.value) {
      return false;
    } else {
      return (
        !ownsProposal.value ||
        proposalHasBeenSubmitted.value ||
        !inEitherSubmissionPeriod.value
      );
    }
  });

  const inEitherSubmissionPeriod = computed(() => {
    if (!proposal.value) {
      return;
    }
    if (proposal.value.shortlisted) {
      return inShortlistSubmissionPeriod.value;
    } else {
      return inSubmissionPeriod.value;
    }
  });

  const postFixingPeriod = computed(() => {
    const now = new Date();
    // If a shortlist submission period exists, and this proposal is shortlisted, we are only postFixingPeriod if the shortlist fixing period has passed
    if (proposal.value?.shortlisted && shortlistDeadline.value) {
      if (shortlistFixingPeriod.value?.end) {
        return new Date(shortlistFixingPeriod.value.end) < now;
      } else {
        return false;
      }
    }
    // Otherwise, just return if the first round fixing period has passed
    if (firstRoundFixingPeriod.value?.end) {
      return new Date(firstRoundFixingPeriod.value.end) < now;
    } else {
      return null;
    }
  });

  const canEditProposalPage = computed(
    () =>
      ownsProposal.value &&
      !proposalHasBeenSubmitted.value &&
      inEitherSubmissionPeriod.value === true,
  );

  // Management mode for a proposal is only ever available to Station A
  const managementModeAccessible = computed(() => {
    return isSuper.value;
  });

  const canViewSubmittedProposal = computed(
    () => ownsProposal.value || isSuper.value || ownsRFP.value,
  );

  return {
    proposalOverallIsReadOnly,
    proposalHasBeenSubmitted,
    canEditProposalPage,
    managementModeAccessible,
    canViewProviderFixing,
    canViewStationAFixing,
    canUseProviderFixingControls,
    canViewSubmittedProposal,
    backendThinksProposalCanBeSubmitted,
    ownsProposal,
    nextDeadline,
    afterDeadline,
    postFixingPeriod,
  };
}
